/**
 * The signed-in person's workload — `GET /me/work-counts` (bata-platform ADR 2103, bata-ui#166
 * W3-4, `docs/adr/9101`).
 *
 * Separate from `/me/capabilities` on purpose (bata-platform#436 ASK-3): capabilities say what a
 * person *may* do and are read once per session; this says how much *waits* and is re-read every
 * minute, so the rail's badge follows the queue without a reload. One read, one cache entry: the
 * badge and "My work" share it, keyed as `useApiQuery` keys every read.
 *
 * **A kind the platform does not count for this person is `null` with its reason**, never a zero:
 * "nothing waiting" and "not yours to see" are different answers, and the badge shows neither
 * a number nor an alarm for the second.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiProblemError, callOperation } from '@/api';
import { myWorkCounts, type WorkCounts } from '@/api/generated';
import type { AlertCountsWithGroups } from '@/api/pending/alert-groups';

/** How often the badge re-reads: once a minute, the contract's own suggestion. */
export const WORK_COUNTS_REFRESH_MS = 60_000;

/** The workload read, re-read every minute while the shell is open. */
export function useWorkCounts(): UseQueryResult<WorkCounts, Error> {
  const { i18n } = useTranslation();
  const language = i18n.language;
  return useQuery<WorkCounts, Error>({
    queryKey: [myWorkCounts.operationId, {}, language],
    queryFn: ({ signal }) => callOperation(myWorkCounts, {}, { language, signal }),
    refetchInterval: WORK_COUNTS_REFRESH_MS,
    // A refusal is an answer (a 404 on an older platform, a 401 signed out): not retried.
    retry: (count, error) => !(error instanceof ApiProblemError && error.status < 500) && count < 1,
  });
}

/**
 * The group counts `/me/work-counts` gains with the alert groups (owner ruling DEC-101, ADR 9040):
 * `alerts.assignedGroups` and `alerts.claimableGroups`, a group being rule × market (bata-platform
 * #496). Typed in `src/api/pending/alert-groups.ts` until the re-pin; read when sent, else ignored.
 */
/** A count the platform sent as a whole number, else `null`. */
function groupCount(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null;
}

/** The group counts of a work-counts answer, each `null` when the platform does not send it. */
export function alertGroupCounts(counts: WorkCounts | undefined): { readonly assigned: number | null; readonly claimable: number | null } {
  const alerts = (counts?.alerts ?? null) as AlertCountsWithGroups | null;
  return { assigned: groupCount(alerts?.assignedGroups), claimable: groupCount(alerts?.claimableGroups) };
}

/** What the rail's "My work" entry shows: nothing, or how many alerts (or groups) wait and whether any is late. */
export type WorkBadge =
  | { readonly kind: 'none' }
  | {
    readonly kind: 'count';
    readonly count: number;
    /** Alerts past due — an alert count even when `count` counts groups. */
    readonly overdue: number;
    /** `count` counts alert groups (DEC-101): the platform sent them. */
    readonly groups?: true;
  };

/**
 * The badge from the counts. Pure. The number is the alert GROUPS assigned to this person when the
 * platform counts them (DEC-101: counters count groups, not alerts), else the alerts assigned to
 * them that are in the queue today; `overdue` (assigned, undecided, past due — snoozed or not)
 * makes it urgent. A person who may not read alerts, or a platform that does not count, gets no
 * badge at all.
 */
export function workBadge(counts: WorkCounts | undefined): WorkBadge {
  const alerts = counts?.alerts ?? null;
  if (alerts === null) return { kind: 'none' };
  const groups = alertGroupCounts(counts).assigned;
  const count = groups ?? alerts.assignedOpen;
  if (count === 0 && alerts.assignedOverdue === 0) return { kind: 'none' };
  return { kind: 'count', count, overdue: alerts.assignedOverdue, ...(groups === null ? {} : { groups: true as const }) };
}
